import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@repo/ui";
import { configurationApi } from "~/api";
import { getErrorMessage, replaceOptimisticItem } from "~/hooks/helpers";
import type { Folder } from "~/types/api";
import { sidebarStorage } from "~/utils/sidebar-storage";
import { useSidebarContext } from "../sidebar-context";

type CreateFolderParams = {
  brandId: number;
  name: string;
  parentFolderId?: number | null;
};

type RenameFolderParams = {
  brandId: number;
  folderId: number;
  name: string;
};

type DeleteFolderParams = {
  brandId: number;
  folderId: number;
};

export const folderKeys = {
  byBrand: (brandId: number) =>
    configurationApi.queryOptions("get", "/api/brand/{brandId}/Folder", {
      params: { path: { brandId } },
    }).queryKey,
};

export const useFolderMutations = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { expandEntity } = useSidebarContext();

  const createFolder = useMutation({
    mutationFn: async ({ brandId, name, parentFolderId }: CreateFolderParams) => {
      const response = await configurationApi.client.POST("/api/brand/{brandId}/Folder", {
        params: { path: { brandId } },
        body: {
          name,
          parentFolderId: parentFolderId ?? null,
          folderTasks: [],
        },
      });

      if (response.error) {
        throw new Error(getErrorMessage(response.error, "Failed to create folder"));
      }

      return response.data;
    },
    onMutate: async (newFolder) => {
      const queryKey = folderKeys.byBrand(newFolder.brandId);
      await queryClient.cancelQueries({ queryKey });

      const previousFolders = queryClient.getQueryData<Folder[]>(queryKey);

      if (previousFolders) {
        const tempFolder: Folder = {
          id: -Date.now(),
          brandId: newFolder.brandId,
          name: newFolder.name,
          parentFolderId: newFolder.parentFolderId ?? null,
          folderTasks: [],
        };

        queryClient.setQueryData<Folder[]>(queryKey, [...previousFolders, tempFolder]);
      }

      return { previousFolders };
    },
    onError: (err, newFolder, context) => {
      const queryKey = folderKeys.byBrand(newFolder.brandId);
      if (context?.previousFolders) {
        queryClient.setQueryData(queryKey, context.previousFolders);
      }
      showToast("error", "Failed to create folder");
    },
    onSuccess: (data, variables) => {
      const queryKey = folderKeys.byBrand(variables.brandId);

      if (data) {
        queryClient.setQueryData<Folder[]>(queryKey, (old) =>
          replaceOptimisticItem(old, {
            matchOptimistic: (f) => f.id < 0 && f.name === variables.name,
            replaceWith: data,
          })
        );

        const updatedFolders = queryClient.getQueryData<Folder[]>(queryKey);
        if (updatedFolders) {
          sidebarStorage.cacheFolders(variables.brandId, updatedFolders);
        }
      }

      const parentEntityId = variables.parentFolderId
        ? `folder-${variables.brandId}-${variables.parentFolderId}`
        : `brand-${variables.brandId}`;
      expandEntity(parentEntityId);

      queryClient.invalidateQueries({ queryKey });
      showToast("success", "Folder created successfully");
    },
  });

  const renameFolder = useMutation({
    mutationFn: async ({ brandId, folderId, name }: RenameFolderParams) => {
      const response = await configurationApi.client.PUT(
        "/api/brand/{brandId}/Folder/{folderId}/rename",
        {
          params: { path: { brandId, folderId } },
          body: { name },
        }
      );

      if (response.error) {
        throw new Error(getErrorMessage(response.error, "Failed to rename folder"));
      }

      return response.data;
    },
    onMutate: async ({ brandId, folderId, name }) => {
      const queryKey = folderKeys.byBrand(brandId);
      await queryClient.cancelQueries({ queryKey });
      const previousFolders = queryClient.getQueryData<Folder[]>(queryKey);

      if (previousFolders) {
        queryClient.setQueryData<Folder[]>(queryKey, (old) => {
          if (!old) return [];
          return old.map((f) => (f.id === folderId ? { ...f, name } : f));
        });
      }
      return { previousFolders };
    },
    onError: (err, vars, context) => {
      const queryKey = folderKeys.byBrand(vars.brandId);
      if (context?.previousFolders) {
        queryClient.setQueryData(queryKey, context.previousFolders);
      }
      showToast("error", "Failed to rename folder");
    },
    onSuccess: (_, vars) => {
      const queryKey = folderKeys.byBrand(vars.brandId);
      const updatedFolders = queryClient.getQueryData<Folder[]>(queryKey);
      if (updatedFolders) {
        sidebarStorage.cacheFolders(vars.brandId, updatedFolders);
      }
      queryClient.invalidateQueries({ queryKey });
      showToast("success", "Folder renamed");
    },
  });

  const deleteFolder = useMutation({
    mutationFn: async ({ brandId, folderId }: DeleteFolderParams) => {
      const response = await configurationApi.client.DELETE(
        "/api/brand/{brandId}/Folder/{folderId}",
        {
          params: { path: { brandId, folderId } },
        }
      );

      if (response.error) {
        throw new Error(getErrorMessage(response.error, "Failed to delete folder"));
      }
      return response.data;
    },
    onMutate: async ({ brandId, folderId }) => {
      const queryKey = folderKeys.byBrand(brandId);
      await queryClient.cancelQueries({ queryKey });
      const previousFolders = queryClient.getQueryData<Folder[]>(queryKey);

      if (previousFolders) {
        queryClient.setQueryData<Folder[]>(queryKey, (old) => {
          if (!old) return [];
          return old.filter((f) => f.id !== folderId);
        });
      }
      return { previousFolders };
    },
    onError: (err, vars, context) => {
      const queryKey = folderKeys.byBrand(vars.brandId);
      if (context?.previousFolders) {
        queryClient.setQueryData(queryKey, context.previousFolders);
      }
      showToast("error", "Failed to delete folder");
    },
    onSuccess: (_, vars) => {
      const queryKey = folderKeys.byBrand(vars.brandId);

      const updatedFolders = queryClient.getQueryData<Folder[]>(queryKey);
      if (updatedFolders) {
        sidebarStorage.cacheFolders(vars.brandId, updatedFolders);
      }
      queryClient.invalidateQueries({ queryKey });
      showToast("success", "Folder deleted");
    },
  });

  return {
    createFolder,
    renameFolder,
    deleteFolder,
  };
};
